import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Download as DownloadIcon, File as FileIcon, Lock, Clock, AlertTriangle, Loader2, Share2, FolderDown,
} from "lucide-react";
import PageWrap from "@/fileshare/components/PageWrap";
import ShareActions from "@/fileshare/components/share/ShareActions";
import api from "@/fileshare/lib/api";
import { formatBytes, formatApiErrorDetail, timeLeft } from "@/fileshare/lib/format";
import { Button } from "@/fileshare/components/ui/button";
import { Input } from "@/fileshare/components/ui/input";
import { Label } from "@/fileshare/components/ui/label";

/** Pull a human-readable error out of an axios error (handles blob responses). */
async function extractErrorDetail(err, fallback) {
  if (err.response?.data instanceof Blob) {
    try {
      return JSON.parse(await err.response.data.text()).detail || fallback;
    } catch (parseErr) {
      console.error("Failed to parse error blob:", parseErr);
      return fallback;
    }
  }
  return formatApiErrorDetail(err.response?.data?.detail) || fallback;
}

/** Trigger a browser download for a blob response. */
function saveBlob(data, filename) {
  const url = window.URL.createObjectURL(new Blob([data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function ErrorState({ error }) {
  return (
    <PageWrap className="max-w-md mx-auto px-6 pt-24 text-center">
      <div className="h-12 w-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
        <AlertTriangle className="h-6 w-6 text-red-500" />
      </div>
      <h1 className="text-xl font-semibold text-slate-900" data-testid="download-error">{error}</h1>
      <p className="text-sm text-slate-500 mt-2">This link may have expired or hit its download limit.</p>
      <Link to="/fileshares"><Button className="mt-6 bg-blue-500 hover:bg-blue-600 text-white">Upload your own file</Button></Link>
    </PageWrap>
  );
}

function FileMetaBadges({ meta }) {
  return (
    <div className="mt-6 flex flex-wrap gap-2 text-xs">
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">
        <Clock className="h-3 w-3" /> {timeLeft(meta.expires_at)}
      </span>
      <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600" data-testid="download-count">
        {meta.download_count} {meta.download_count === 1 ? "download" : "downloads"}
        {meta.max_downloads != null ? ` / ${meta.max_downloads}` : ""}
      </span>
      {meta.has_password && (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700"><Lock className="h-3 w-3" /> Protected</span>
      )}
    </div>
  );
}

function BatchFileList({ files, downloadingId, canDownload, onDownloadFile }) {
  return (
    <div className="mt-6 rounded-md border border-slate-100 divide-y divide-slate-50 max-h-64 overflow-y-auto" data-testid="batch-files-list">
      {files.map((f) => (
        <div key={f.id} className="flex items-center justify-between px-3 py-2.5 gap-3" data-testid={`batch-file-${f.id}`}>
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="h-8 w-8 rounded bg-blue-50 flex items-center justify-center shrink-0">
              <FileIcon className="h-4 w-4 text-blue-500" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{f.original_filename}</p>
              <p className="text-xs text-slate-400">{formatBytes(f.file_size)}</p>
            </div>
          </div>
          <Button
            data-testid={`batch-file-download-${f.id}`}
            variant="ghost"
            size="icon"
            disabled={!canDownload || downloadingId === f.id}
            onClick={() => onDownloadFile(f)}
            className="text-slate-400 hover:text-blue-500 h-8 w-8 shrink-0"
          >
            {downloadingId === f.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <DownloadIcon className="h-4 w-4" />}
          </Button>
        </div>
      ))}
    </div>
  );
}

export default function Download() {
  const { slug } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/share/${slug}`);
        setMeta(data);
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || "File not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const isBatch = meta?.type === "batch";
  const passwordOk = !meta?.has_password || password.length > 0;

  const verifyPassword = async () => {
    if (!meta.has_password) return true;
    const endpoint = isBatch ? `/batches/${slug}/verify-password` : `/files/${slug}/verify-password`;
    const { data } = await api.post(endpoint, { password });
    if (!data.valid) toast.error("Incorrect password");
    return data.valid;
  };

  const bumpCount = () => setMeta((m) => ({ ...m, download_count: m.download_count + 1 }));

  /** Handle either a blob response (legacy local file) OR a JSON
   *  {download_url} response (new R2-backed files). */
  const followDownloadResponse = (res, fallbackFilename) => {
    const ct = (res.headers && (res.headers["content-type"] || res.headers["Content-Type"])) || "";
    if (ct.includes("application/json")) {
      // JSON with a presigned URL — hand off to the browser so the browser
      // streams the file straight from R2. We use a hidden anchor so the
      // Content-Disposition header on R2 takes effect for the filename.
      const url = res.data.download_url;
      if (!url) throw new Error("Missing download URL in response");
      const a = document.createElement("a");
      a.href = url;
      a.rel = "noopener";
      // Some browsers ignore `download` for cross-origin — R2 sets
      // Content-Disposition on the presigned URL so the filename is right.
      a.download = fallbackFilename || "";
      document.body.appendChild(a);
      a.click();
      a.remove();
      return;
    }
    saveBlob(res.data, fallbackFilename);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      if (!(await verifyPassword())) return;
      const endpoint = isBatch ? `/batches/${slug}/download` : `/files/${slug}/download`;
      const res = await api.post(endpoint, { password }, { responseType: "blob" });
      // Convert the blob back to JSON if the server returned JSON.
      let parsed = res;
      const ct = (res.headers["content-type"] || "").toLowerCase();
      if (ct.includes("application/json")) {
        const text = await res.data.text();
        parsed = { ...res, data: JSON.parse(text) };
      }
      followDownloadResponse(parsed, isBatch ? `fileshare-${slug}.zip` : meta.original_filename);
      toast.success("Download started");
      bumpCount();
    } catch (err) {
      toast.error(await extractErrorDetail(err, "Download failed"));
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadFile = async (f) => {
    setDownloadingId(f.id);
    try {
      if (!(await verifyPassword())) return;
      const res = await api.post(`/batches/${slug}/files/${f.id}/download`, { password }, { responseType: "blob" });
      let parsed = res;
      const ct = (res.headers["content-type"] || "").toLowerCase();
      if (ct.includes("application/json")) {
        const text = await res.data.text();
        parsed = { ...res, data: JSON.parse(text) };
      }
      followDownloadResponse(parsed, f.original_filename);
      toast.success(`Downloading ${f.original_filename}`);
      bumpCount();
    } catch (err) {
      toast.error(await extractErrorDetail(err, "Download failed"));
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return <PageWrap className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-6 w-6 text-blue-500 animate-spin" /></PageWrap>;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  const title = isBatch ? `${meta.file_count} files` : meta.original_filename;

  return (
    <PageWrap className="max-w-lg mx-auto px-6 pt-20 pb-24">
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="bg-white border border-slate-200 rounded-lg shadow-[0_2px_12px_rgba(0,0,0,0.05)] p-8"
        data-testid="download-card"
      >
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
            {isBatch
              ? <FolderDown className="h-7 w-7 text-blue-500" strokeWidth={1.5} />
              : <FileIcon className="h-7 w-7 text-blue-500" strokeWidth={1.5} />}
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-slate-900 truncate" data-testid="download-filename">
              {isBatch ? `${meta.file_count} files shared with you` : meta.original_filename}
            </h1>
            <p className="text-sm text-slate-500">{formatBytes(isBatch ? meta.total_size : meta.file_size)}</p>
          </div>
        </div>

        <FileMetaBadges meta={meta} />

        {meta.has_password && (
          <div className="mt-6">
            <Label className="text-sm text-slate-700">Enter password</Label>
            <Input
              data-testid="download-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password required"
              className="mt-1.5 bg-slate-50/60 border-slate-200"
            />
          </div>
        )}

        {isBatch && (
          <BatchFileList
            files={meta.files}
            downloadingId={downloadingId}
            canDownload={passwordOk}
            onDownloadFile={handleDownloadFile}
          />
        )}

        <motion.div whileTap={{ scale: 0.99 }}>
          <Button
            data-testid="download-button"
            onClick={handleDownload}
            disabled={downloading || !passwordOk}
            className="mt-6 w-full bg-blue-500 hover:bg-blue-600 text-white h-12 text-[15px] gap-2"
          >
            {isBatch ? <FolderDown className="h-4 w-4" /> : <DownloadIcon className="h-4 w-4" />}
            {downloading ? "Preparing…" : isBatch ? "Download all (.zip)" : "Download file"}
          </Button>
        </motion.div>

        <ShareActions url={window.location.href} title={title} />
      </motion.div>

      <Link to="/fileshares" className="mt-8 flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-slate-600">
        <Share2 className="h-4 w-4" /> Powered by FileShare
      </Link>
    </PageWrap>
  );
}
